import { ReactiveUtil } from '@webapp/util/ReactiveUtil'
import { DateTime } from 'luxon'
import { isDefined } from './TypeScriptUtil'
import _ from 'lodash'
import { ApiError } from './Api'
import { markRaw } from 'vue'

/**
 * Data structure intended to be wrapped in a reactive proxy. Components can request data operations at any time
 * but requests that occur while another request is pending are ignored. Notification of changes (either direct
 * or as a result of a completed data operation) occurs via the proxy.
 */
export class AsyncData<T> {
    readonly key: string
    readonly typeName: string
    state = AsyncDataState.NotLoaded    

    /**
     * The immutable data object that was loaded or saved. Changes should only occur by replacing the object and 
     * updating state and asOfDate.
     */
    data?: Readonly<T>

    asOfDate?: DateTime

    /**
     * Indicates whether the data is known to be out of date. Set when current data is invalidated but not immediately
     * reloaded. May be true in any async state. Cleared when any load is completed successfully (no error).
     */
    stale = false

    readonly whenLoadCompleted: Promise<Readonly<T> | undefined> | Readonly<T> | undefined

    setWhenLoadCompleted(value: Promise<Readonly<T> | undefined> | Readonly<T> | undefined) { 
        (this as { whenLoadCompleted: Promise<T | undefined> | T | undefined }).whenLoadCompleted = value
    }

    constructor(key: string, typeName = '') {
        this.key = key
        this.typeName = typeName
    }

    static getRelatedKeys<T>(asyncDatas: AsyncData<T>[], getKey: (d: Readonly<T>) => string) {
        return asyncDatas.map(a => a.data).filter(isDefined).map(getKey)
    }

    /**
     * Gets a value indicating whether an attempt should be made to (re-)load this item, based on its current async state and 
     * the specified load mode. This method should be used to skip loading if a load or save is in progress, or if a previous 
     * load failed.
     */
    shouldLoad(loadMode = LoadMode.EnsureLoaded) {
        if (loadMode == LoadMode.TrackOnly)
            return false
        
        const reload = loadMode == LoadMode.Reload
        const reloadText = reload ? "should" : "should not"

        switch (this.state) {
            case AsyncDataState.NotLoaded:
                //console.log(`${this.typeName}[${this.key}]: ${this.state}, should load`)
                return true // initial load (nothing to lose) or known to be out of date

            case AsyncDataState.LoadFailed:
            case AsyncDataState.NotFound:
            case AsyncDataState.Loaded:
                if (reload) {
                    console.log(`${this.typeName}[${this.key}]: ${this.state}, ${reloadText} reload`)
                }
                return this.stale || reload // only if stale or reload requested

            case AsyncDataState.Loading:
            case AsyncDataState.Saving: // includes an implicit load after save
                // already (re-)loading, so don't send another request
                //console.log(`${this.typeName}[${this.key}]: Already ${this.state}`)
                return false

            default:
                throw "AsyncData.UnknownState"
        }
    }

    shouldLoadPartial<PT extends T & PartialData>(this: AsyncData<PT>, loadMode = LoadMode.EnsureLoaded) {
        return this.shouldLoad(loadMode) || (loadMode == LoadMode.EnsureLoaded && this.isLoaded && !this.data?.isComplete)
    }

    get isLoaded() { return this.state == AsyncDataState.Loaded }

    /**
     * Gets a value indicating whether an attempt was made to load the data, and the attempt completed in the Loaded, NotFound, or LoadFailed state.
     * By default, also checks that the data is not stale. Pass allowStale=true to skip the staleness check.
     */
    isLoadComplete(allowStale = false) { 
        return completedStates.includes(this.state) && (allowStale || !this.stale)
    }

    /**
     * Tracks asynchronous loading of an item, updating the AsyncData structure (especially its state) before and after. State changes can 
     * be used to prevent concurrent requests from trying to update the same item.
     * @param asyncData reactive proxy of AsyncData structure being loaded
     * @param request promise that returns the data OR throws a data error
     */
    static trackLoad<T>(asyncData: AsyncData<T>, requestTime: DateTime, request: Promise<T>) {
        ReactiveUtil.checkIsMutableProxy(asyncData)
        // HACK: This throws when loading permissions on the FG member tab, no idea why
        //checkNoRequestsPending(asyncData)

        const oldState = asyncData.state

        //console.log(`${asyncData.typeName}[${asyncData.key}]: Loading`)
        asyncData.state = AsyncDataState.Loading
        asyncData.setWhenLoadCompleted(request.then(data => {
            //console.log(`${asyncData.typeName}[${asyncData.key}]: Got data`) // ${JSON.stringify(loadedData)}`)

            AsyncData.finishLoading(asyncData, data, requestTime)
            return data
        }, err => {
            const errCode = (err instanceof ApiError) ? err.statusCode : undefined
            if (errCode == DataError.NotFound) {
                asyncData.state = AsyncDataState.NotFound
                asyncData.stale = false // this is the latest known value
                console.log(`${asyncData.typeName}[${asyncData.key}]: Not found`)
            }
            else if (oldState == AsyncDataState.Loaded) {
                // old data is best available, so revert to old state
                asyncData.state = oldState
            } else {
                // old data is not valid (as far as we know), so enter failed state
                asyncData.state = AsyncDataState.LoadFailed
                console.log(`${asyncData.typeName}[${asyncData.key}]: Load failed: `, err)
            }
            asyncData.setWhenLoadCompleted(asyncData.data) // discard promise
            
            // do not rethrow -- promise only indicates completion, not success
            return undefined
        }))
    }

    static load<T>(asyncData: AsyncData<T>, data: T, requestTime: DateTime, force = false) {
        ReactiveUtil.checkIsMutableProxy(asyncData)

        // don't touch if the data is already loaded and new data is not any newer (avoid unnecessary reactivity triggers)
        // NOTE: this can happen if the same item is loaded inline multiple times in the same request
        if (!force && asyncData.state == AsyncDataState.Loaded && asyncData.asOfDate && asyncData.asOfDate >= requestTime) {
            return
        }

        asyncData.state = AsyncDataState.Loading
        AsyncData.finishLoading(asyncData, data, requestTime)
    }

    private static finishLoading<T>(asyncData: AsyncData<T>, data: T, requestTime: DateTime) {
        if (!isDefined(data))
            throw new Error('AsyncData: cannot load undefined data (should be marked NotFound instead)')

        // HACK: TS can't figure out that data is an object AND not null, because in JS, typeof null === 'object', so just cast it
        asyncData.data = (typeof data === 'object') ? markRaw(data as unknown as object) as NonNullable<T> : data
        //console.log(`${asyncData.typeName}[${asyncData.key}]: Loaded`)
    
        asyncData.state = AsyncDataState.Loaded
        asyncData.asOfDate = markRaw(requestTime) // treat as an immutable primitive
        asyncData.stale = false
        asyncData.setWhenLoadCompleted(asyncData.data) // discard promise
    }

    static async invalidate<T>(asyncData: AsyncData<T> | undefined) {
        if (asyncData) {
            ReactiveUtil.checkIsMutableProxy(asyncData)

            // NOTE: Do not clear old data since it is still the best available, and we don't want to thrash the UI while
            // waiting for the new data to arrive.
            asyncData.stale = true
        }
    }

    static async setDeleted<T>(asyncData: AsyncData<T> | undefined) {
        if (asyncData) {
            ReactiveUtil.checkIsMutableProxy(asyncData)

            asyncData.state = AsyncDataState.NotFound
            asyncData.data = undefined
            asyncData.asOfDate = undefined
            asyncData.setWhenLoadCompleted(undefined)        
        }
    }
}

function checkNoRequestsPending<T>(a: AsyncData<T>) {
    if (a.state == AsyncDataState.Loading || 
        a.state == AsyncDataState.Saving) {
        throw "AsyncData.RequestPending"
    }
}


export interface VersionedData {
    modifiedDate?: DateTime
}

export interface PartialData {
    isComplete: boolean
}

export enum AsyncDataState {
    NotLoaded = "NotLoaded",
    Loading = "Loading",
    Loaded = "Loaded",
    Saving = "Saving",
    NotFound = "NotFound",
    LoadFailed = "LoadFailed",
}

const completedStates = [AsyncDataState.Loaded, AsyncDataState.NotFound, AsyncDataState.LoadFailed]

export enum LoadMode {
    TrackOnly = "TrackOnly",
    EnsureLoaded = "EnsureLoaded",
    Reload = "Reload",
}

export enum DataError {
    BadRequest = 400, // HTTP 4xx
    Forbidden = 403, // HTTP 403
    NotFound = 404, // HTTP 404
    Conflict = 412, // HTTP 412
    Internal = 500, // HTTP 5xx
}
